import React, { useState } from 'react';
import { 
  Shield, FileText, Calendar, Download, RefreshCw, 
  CheckCircle, XCircle, AlertTriangle, Clock, Eye,
  TrendingUp, TrendingDown, BarChart3, PieChart,
  Users, Activity, Target, Award, Zap, Settings,
  Bell, Send, Printer, Share2, Filter, Search
} from 'lucide-react';

const ComplianceReporting = ({ onClose }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedReport, setSelectedReport] = useState(null);
  const [filterPeriod, setFilterPeriod] = useState('current');
  const [filterStatus, setFilterStatus] = useState('all');
  const [loading, setLoading] = useState(false);
  const [notifications, setNotifications] = useState([]);

  // Mock compliance data
  const nqipData = {
    title: 'National Quality Indicators Program (NQIP)',
    period: 'Q2 2024',
    status: 'Compliant',
    score: 94.2,
    target: 90,
    trends: {
      pressureInjuries: { current: 2.1, previous: 3.4, trend: '-38.2%', status: 'improving' },
      falls: { current: 1.8, previous: 2.5, trend: '-28.0%', status: 'improving' },
      medicationErrors: { current: 0.9, previous: 1.2, trend: '-25.0%', status: 'improving' },
      infections: { current: 1.2, previous: 1.8, trend: '-33.3%', status: 'improving' },
      restraintUse: { current: 0.5, previous: 0.8, trend: '-37.5%', status: 'improving' }
    },
    indicators: [
      {
        id: 'NQIP-001',
        name: 'Pressure Injury Prevention',
        category: 'Safety',
        current: 2.1,
        target: 3.0,
        status: 'Compliant',
        trend: 'down',
        lastUpdated: '2024-07-20',
        description: 'Rate of pressure injuries per 1000 resident days'
      },
      {
        id: 'NQIP-002',
        name: 'Falls Prevention',
        category: 'Safety',
        current: 1.8,
        target: 2.5,
        status: 'Compliant',
        trend: 'down',
        lastUpdated: '2024-07-20',
        description: 'Rate of falls per 1000 resident days'
      },
      {
        id: 'NQIP-003',
        name: 'Medication Safety',
        category: 'Clinical',
        current: 0.9,
        target: 1.5,
        status: 'Compliant',
        trend: 'down',
        lastUpdated: '2024-07-20',
        description: 'Rate of medication errors per 1000 doses'
      },
      {
        id: 'NQIP-004',
        name: 'Infection Control',
        category: 'Clinical',
        current: 1.2,
        target: 2.0,
        status: 'Compliant',
        trend: 'down',
        lastUpdated: '2024-07-20',
        description: 'Rate of healthcare-associated infections'
      },
      {
        id: 'NQIP-005',
        name: 'Restraint Use',
        category: 'Care',
        current: 0.5,
        target: 1.0,
        status: 'Compliant',
        trend: 'down',
        lastUpdated: '2024-07-20',
        description: 'Rate of physical restraint use'
      }
    ]
  };

  const sirsData = {
    title: 'Serious Incident Response Scheme (SIRS)',
    period: 'Q2 2024',
    status: 'Compliant',
    score: 98.5,
    target: 95,
    trends: {
      reportableIncidents: { current: 3, previous: 7, trend: '-57.1%', status: 'improving' },
      responseTime: { current: 2.1, previous: 3.8, trend: '-44.7%', status: 'improving' },
      resolutionRate: { current: 96.7, previous: 89.2, trend: '+8.4%', status: 'improving' },
      followUpActions: { current: 94.1, previous: 87.5, trend: '+7.5%', status: 'improving' }
    },
    incidents: [
      {
        id: 'SIRS-001',
        type: 'Fall with Injury',
        severity: 'High',
        reported: '2024-07-15',
        resolved: '2024-07-18',
        status: 'Resolved',
        responseTime: '2.1 hours',
        followUpActions: 3,
        description: 'Resident fall resulting in minor injury, immediate response and care plan updated'
      },
      {
        id: 'SIRS-002',
        type: 'Medication Error',
        severity: 'Medium',
        reported: '2024-07-10',
        resolved: '2024-07-12',
        status: 'Resolved',
        responseTime: '1.8 hours',
        followUpActions: 2,
        description: 'Wrong medication administered, no harm caused, protocols reviewed'
      },
      {
        id: 'SIRS-003',
        type: 'Aggressive Behavior',
        severity: 'High',
        reported: '2024-07-08',
        resolved: '2024-07-11',
        status: 'Resolved',
        responseTime: '2.5 hours',
        followUpActions: 4,
        description: 'Resident aggressive behavior, de-escalation techniques applied'
      }
    ]
  };

  const auditData = {
    title: 'Compliance Audits',
    period: 'Q2 2024',
    status: 'Compliant',
    score: 96.8,
    target: 95,
    audits: [
      {
        id: 'AUD-001',
        type: 'Medication Management',
        date: '2024-07-18',
        status: 'Passed',
        score: 98.2,
        findings: 2,
        recommendations: 3,
        nextAudit: '2024-10-18'
      },
      {
        id: 'AUD-002',
        type: 'Infection Control',
        date: '2024-07-15',
        status: 'Passed',
        score: 95.8,
        findings: 1,
        recommendations: 2,
        nextAudit: '2024-10-15'
      },
      {
        id: 'AUD-003',
        type: 'Documentation',
        date: '2024-07-12',
        status: 'Passed',
        score: 97.5,
        findings: 0,
        recommendations: 1,
        nextAudit: '2024-10-12'
      },
      {
        id: 'AUD-004',
        type: 'Staff Training',
        date: '2024-07-10',
        status: 'Passed',
        score: 96.1,
        findings: 1,
        recommendations: 2,
        nextAudit: '2024-10-10'
      }
    ]
  };

  const showNotification = (message, type) => {
    const id = Date.now();
    setNotifications(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }, 5000);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Compliant': return 'text-green-600 bg-green-100';
      case 'Non-Compliant': return 'text-red-600 bg-red-100';
      case 'At Risk': return 'text-yellow-600 bg-yellow-100';
      case 'Passed': return 'text-green-600 bg-green-100';
      case 'Failed': return 'text-red-600 bg-red-100';
      case 'Resolved': return 'text-green-600 bg-green-100';
      case 'Under Investigation': return 'text-blue-600 bg-blue-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'High': return 'text-red-600 bg-red-100';
      case 'Medium': return 'text-yellow-600 bg-yellow-100';
      case 'Low': return 'text-green-600 bg-green-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getTrendIcon = (trend) => {
    return trend === 'up' ? 
      <TrendingUp className="w-4 h-4 text-green-500" /> : 
      <TrendingDown className="w-4 h-4 text-red-500" />;
  };

  const handleGenerateReport = (reportType) => {
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      showNotification(`${reportType} report generated successfully`, 'success');
    }, 2000);
  };

  const handleExportReport = (reportType) => {
    console.log('Exporting report:', reportType);
    showNotification(`${reportType} report exported successfully`, 'success');
  };

  const handleScheduleAudit = () => {
    console.log('Scheduling new audit');
    showNotification('New audit scheduled successfully', 'success');
  };

  const handleViewDetails = (item) => {
    setSelectedReport(item);
    showNotification(`Viewing details for ${item.name || item.type}`, 'info');
  };

  if (!onClose) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl p-6 max-w-7xl w-full mx-4 max-h-[90vh] overflow-y-auto shadow-xl">
        {/* Notifications */}
        <div className="fixed top-4 right-4 z-[100]">
          {notifications.map(notification => (
            <div
              key={notification.id}
              className={`p-4 mb-2 rounded-lg shadow-md text-white flex items-center gap-3 ${
                notification.type === 'success' ? 'bg-green-500' :
                notification.type === 'error' ? 'bg-red-500' : 'bg-blue-500'
              }`}
            >
              {notification.type === 'success' && <CheckCircle className="w-5 h-5" />}
              {notification.type === 'error' && <XCircle className="w-5 h-5" />}
              {notification.type === 'info' && <Shield className="w-5 h-5" />}
              <span>{notification.message}</span>
            </div>
          ))}
        </div>

        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-3">
            <Shield className="w-7 h-7 text-blue-600" />
            Compliance Reporting (NQIP & SIRS)
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl"
          >
            ×
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="flex flex-wrap gap-2 mb-6">
          {[
            { id: 'overview', label: 'Overview', icon: BarChart3 },
            { id: 'nqip', label: 'NQIP', icon: Target },
            { id: 'sirs', label: 'SIRS', icon: AlertTriangle },
            { id: 'audits', label: 'Audits', icon: FileText }
          ].map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
                  activeTab === tab.id
                    ? 'bg-blue-600 text-white'
                    : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Compliance Summary */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-lg border border-green-200">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-3 bg-green-500 rounded-xl">
                    <Target className="w-6 h-6 text-white" />
                  </div>
                  <span className="text-2xl font-bold text-green-800">{nqipData.score}%</span>
                </div>
                <h3 className="font-semibold text-green-800 mb-2">NQIP Score</h3>
                <p className="text-sm text-green-600">Target: {nqipData.target}%</p>
                <div className="flex items-center gap-2 mt-2">
                  <TrendingUp className="w-4 h-4 text-green-600" />
                  <span className="text-sm text-green-600">+4.2% from last quarter</span>
                </div>
              </div>

              <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-lg border border-blue-200">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-3 bg-blue-500 rounded-xl">
                    <AlertTriangle className="w-6 h-6 text-white" />
                  </div>
                  <span className="text-2xl font-bold text-blue-800">{sirsData.score}%</span>
                </div>
                <h3 className="font-semibold text-blue-800 mb-2">SIRS Score</h3>
                <p className="text-sm text-blue-600">Target: {sirsData.target}%</p>
                <div className="flex items-center gap-2 mt-2">
                  <TrendingUp className="w-4 h-4 text-blue-600" />
                  <span className="text-sm text-blue-600">+3.5% from last quarter</span>
                </div>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-6 rounded-lg border border-purple-200">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-3 bg-purple-500 rounded-xl">
                    <FileText className="w-6 h-6 text-white" />
                  </div>
                  <span className="text-2xl font-bold text-purple-800">{auditData.score}%</span>
                </div>
                <h3 className="font-semibold text-purple-800 mb-2">Audit Score</h3>
                <p className="text-sm text-purple-600">Target: {auditData.target}%</p>
                <div className="flex items-center gap-2 mt-2">
                  <TrendingUp className="w-4 h-4 text-purple-600" />
                  <span className="text-sm text-purple-600">+1.8% from last quarter</span>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Quick Actions</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <button
                  onClick={() => handleGenerateReport('NQIP')}
                  disabled={loading}
                  className={`p-4 rounded-lg border-2 border-dashed border-gray-300 hover:border-green-500 hover:bg-green-50 transition-colors ${
                    loading ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  <Target className="w-8 h-8 text-green-600 mx-auto mb-2" />
                  <p className="text-sm font-medium text-gray-700">Generate NQIP Report</p>
                </button>
                
                <button
                  onClick={() => handleGenerateReport('SIRS')}
                  disabled={loading}
                  className={`p-4 rounded-lg border-2 border-dashed border-gray-300 hover:border-blue-500 hover:bg-blue-50 transition-colors ${
                    loading ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  <AlertTriangle className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                  <p className="text-sm font-medium text-gray-700">Generate SIRS Report</p>
                </button>
                
                <button
                  onClick={handleScheduleAudit}
                  className="p-4 rounded-lg border-2 border-dashed border-gray-300 hover:border-purple-500 hover:bg-purple-50 transition-colors"
                >
                  <Calendar className="w-8 h-8 text-purple-600 mx-auto mb-2" />
                  <p className="text-sm font-medium text-gray-700">Schedule Audit</p>
                </button>
                
                <button
                  onClick={() => handleExportReport('Compliance Summary')}
                  className="p-4 rounded-lg border-2 border-dashed border-gray-300 hover:border-orange-500 hover:bg-orange-50 transition-colors"
                >
                  <Download className="w-8 h-8 text-orange-600 mx-auto mb-2" />
                  <p className="text-sm font-medium text-gray-700">Export Summary</p>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* NQIP Tab */}
        {activeTab === 'nqip' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-800">NQIP Indicators</h3>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600">Period: {nqipData.period}</span>
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusColor(nqipData.status)}`}>
                      {nqipData.status}
                    </span>
                  </div>
                </div>
              </div>
              
              <div className="p-6">
                <div className="space-y-4">
                  {nqipData.indicators.map((indicator) => (
                    <div key={indicator.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h4 className="font-medium text-gray-900">{indicator.name}</h4>
                          <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusColor(indicator.status)}`}>
                            {indicator.status}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mb-2">{indicator.description}</p>
                        <div className="flex items-center gap-4 text-sm">
                          <span className="text-gray-600">Current: <strong>{indicator.current}</strong></span>
                          <span className="text-gray-600">Target: <strong>{indicator.target}</strong></span>
                          <span className="text-gray-600">Updated: {indicator.lastUpdated}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-24 bg-gray-200 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full ${
                              indicator.current <= indicator.target ? 'bg-green-500' : 'bg-red-500'
                            }`}
                            style={{width: `${Math.min(100, (indicator.current / indicator.target) * 100)}%`}}
                          ></div>
                        </div>
                        <button
                          onClick={() => handleViewDetails(indicator)}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* SIRS Tab */}
        {activeTab === 'sirs' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-800">SIRS Incidents</h3>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600">Period: {sirsData.period}</span>
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusColor(sirsData.status)}`}>
                      {sirsData.status}
                    </span>
                  </div>
                </div>
              </div>
              
              <div className="p-6">
                <div className="space-y-4">
                  {sirsData.incidents.map((incident) => (
                    <div key={incident.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h4 className="font-medium text-gray-900">{incident.type}</h4>
                          <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getSeverityColor(incident.severity)}`}>
                            {incident.severity}
                          </span>
                          <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusColor(incident.status)}`}>
                            {incident.status}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mb-2">{incident.description}</p>
                        <div className="flex items-center gap-4 text-sm">
                          <span className="text-gray-600">Reported: {incident.reported}</span>
                          <span className="text-gray-600">Resolved: {incident.resolved}</span>
                          <span className="text-gray-600">Response Time: {incident.responseTime}</span>
                          <span className="text-gray-600">Actions: {incident.followUpActions}</span>
                        </div>
                      </div>
                      <button
                        onClick={() => handleViewDetails(incident)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Audits Tab */}
        {activeTab === 'audits' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-800">Compliance Audits</h3>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600">Period: {auditData.period}</span>
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusColor(auditData.status)}`}>
                      {auditData.status}
                    </span>
                  </div>
                </div>
              </div>
              
              <div className="p-6">
                <div className="space-y-4">
                  {auditData.audits.map((audit) => (
                    <div key={audit.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h4 className="font-medium text-gray-900">{audit.type}</h4>
                          <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusColor(audit.status)}`}>
                            {audit.status}
                          </span>
                          <span className="text-sm font-semibold text-gray-700">{audit.score}%</span>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-gray-600">
                          <span>Date: {audit.date}</span>
                          <span>Findings: {audit.findings}</span>
                          <span>Recommendations: {audit.recommendations}</span>
                          <span>Next: {audit.nextAudit}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-24 bg-gray-200 rounded-full h-2">
                          <div 
                            className="h-2 rounded-full bg-green-500"
                            style={{width: `${audit.score}%`}}
                          ></div>
                        </div>
                        <button
                          onClick={() => handleViewDetails(audit)}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Report Detail Modal */}
        {selectedReport && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-60">
            <div className="bg-white rounded-2xl p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto shadow-xl">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-bold text-gray-800">Report Details</h3>
                <button
                  onClick={() => setSelectedReport(null)}
                  className="text-gray-500 hover:text-gray-700 text-2xl"
                >
                  ×
                </button>
              </div>
              
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-2">Basic Information</h4>
                    <div className="space-y-2 text-sm">
                      <p><strong>Name:</strong> {selectedReport.name || selectedReport.type}</p>
                      <p><strong>Status:</strong> <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusColor(selectedReport.status)}`}>{selectedReport.status}</span></p>
                      <p><strong>Score:</strong> {selectedReport.score || selectedReport.current}%</p>
                      <p><strong>Target:</strong> {selectedReport.target}%</p>
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-2">Additional Details</h4>
                    <div className="space-y-2 text-sm">
                      <p><strong>Category:</strong> {selectedReport.category}</p>
                      <p><strong>Last Updated:</strong> {selectedReport.lastUpdated || selectedReport.reported}</p>
                      <p><strong>Description:</strong> {selectedReport.description}</p>
                    </div>
                  </div>
                </div>
                
                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => setSelectedReport(null)}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Close
                  </button>
                  <button
                    onClick={() => handleExportReport(selectedReport.name || selectedReport.type)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Export Report
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ComplianceReporting;
